import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import DashboardLayout from "../../components/DashboardLayout";
import { SERVICES, PACKAGES } from "../../data/staticData";
import { SERVICE_FORMS } from "../../data/serviceForms";
import { estimatedCompletionDate } from "../../lib/pricing";
import { api } from "../../lib/api";
import { CheckCircle2, Loader2, ArrowUpRight, ChevronLeft } from "lucide-react";
import { toast } from "sonner";

const MAX_POLLS = 40;             // ~90s total (Stripe webhooks can take up to 60s in production)
const POLL_INTERVAL_MS = 2200;
const AUTO_REFRESH_AFTER_MS = 25000; // if the "still processing" state stays too long, auto-refresh once

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const sessionId = params.get("session_id");

  const [phase, setPhase] = useState("verifying"); // verifying | paid | failed
  const [ctx, setCtx] = useState(null); // /payments/session response
  const [requirements, setRequirements] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [orderCreated, setOrderCreated] = useState(false);
  const pollsRef = useRef(0);

  // 1. Poll payment status until "paid"
  useEffect(() => {
    if (!sessionId) { setPhase("failed"); return; }
    let cancelled = false;
    const check = async () => {
      pollsRef.current += 1;
      try {
        const { data } = await api.get(`/payments/session/${sessionId}`);
        if (cancelled) return;
        setCtx(data);
        if (data.payment_status === "paid") {
          setPhase("paid");
          if (data.order_id) setOrderCreated(true);
          return;
        }
      } catch (err) {
        if (cancelled) return;
        if (err.response?.status === 403 || err.response?.status === 404) {
          setPhase("failed"); return;
        }
      }
      if (pollsRef.current >= MAX_POLLS) {
        setPhase("failed");
        // one-shot auto-refresh after a delay so webhooks that land just after we give up still get picked up
        setTimeout(() => {
          if (!cancelled) window.location.reload();
        }, AUTO_REFRESH_AFTER_MS);
        return;
      }
      setTimeout(check, POLL_INTERVAL_MS);
    };
    check();
    return () => { cancelled = true; };
  }, [sessionId]);

  const service = useMemo(() => (ctx ? SERVICES.find((s) => s.slug === ctx.service_slug) : null), [ctx]);
  const pkg = useMemo(() => (ctx ? (PACKAGES[ctx.service_slug] || []).find((p) => p.id === ctx.package_id) : null), [ctx]);
  const form = useMemo(() => (ctx ? SERVICE_FORMS[ctx.service_slug] : null), [ctx]);
  const eta = pkg ? estimatedCompletionDate(pkg.turnaround) : "";

  const setField = (key, value) => setRequirements((r) => ({ ...r, [key]: value }));

  const submitBrief = async (e) => {
    e.preventDefault();
    if (orderCreated) return;
    const missing = (form?.fields || []).filter((f) => f.required && !requirements[f.key]);
    if (missing.length) {
      toast.error(`Please fill: ${missing.map((m) => m.label).join(", ")}`);
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/orders", {
        service_slug: ctx.service_slug,
        package_id: ctx.package_id,
        package_name: ctx.package_name,
        price: ctx.amount / 100,
        requirements,
        session_id: sessionId,
      });
      setOrderCreated(true);
      toast.success("Order confirmed! Redirecting to your order…");
      setTimeout(() => nav(`/dashboard/orders/${data.order_id}`), 900);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create order");
      setSubmitting(false);
    }
  };

  if (phase === "verifying") {
    return (
      <DashboardLayout>
        <div className="max-w-xl mx-auto py-20 text-center">
          <Loader2 size={40} className="animate-spin text-terra mx-auto" />
          <div className="font-display text-2xl font-bold tracking-tight mt-6">Confirming your payment…</div>
          <p className="text-inkSoft text-sm mt-2">This usually takes a few seconds. Please don&apos;t close this tab.</p>
          <div className="mt-6 inline-flex items-center gap-2 text-xs text-inkSoft bg-surfaceAlt/60 border border-line rounded-full px-3 py-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
            Securely verifying with Stripe · your card has already been charged
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (phase === "failed") {
    return (
      <DashboardLayout>
        <div className="max-w-xl mx-auto py-16 text-center">
          <div className="h-12 w-12 rounded-full bg-terra/15 border border-terra/40 grid place-items-center mx-auto">
            <Loader2 size={22} className="text-terra animate-spin" />
          </div>
          <div className="font-display text-2xl font-bold tracking-tight mt-6">Payment received — just finalising your order</div>
          <p className="text-inkSoft text-sm mt-3 max-w-md mx-auto">
            Stripe is taking a little longer than usual to confirm. Your card has been charged safely and no action is
            needed — your order will appear in <span className="text-ink">My Orders</span> within a minute.
          </p>
          <p className="text-inkSoft text-xs mt-3">Session: <span className="font-mono">{sessionId?.slice(0, 24)}…</span></p>
          <div className="mt-8 flex items-center justify-center gap-3">
            <button onClick={() => window.location.reload()} className="btn-secondary text-sm" data-testid="payment-retry">Check again</button>
            <Link to="/dashboard/orders" className="btn-primary text-sm">Open My Orders</Link>
          </div>
          <p className="text-[11px] text-inkSoft mt-6">If this screen is still here in 2 minutes, email <a href="mailto:contact@zenithranking.com" className="text-terra">contact@zenithranking.com</a> with the session ID above — we&apos;ll sort it out immediately.</p>
        </div>
      </DashboardLayout>
    );
  }

  // phase === "paid"
  if (orderCreated && ctx?.order_id) {
    return (
      <DashboardLayout>
        <div className="max-w-xl mx-auto py-20 text-center">
          <CheckCircle2 size={40} className="text-emerald-500 mx-auto" />
          <div className="font-display text-2xl font-bold tracking-tight mt-6">Order already created</div>
          <p className="text-inkSoft text-sm mt-2">You&apos;ve already submitted the brief for this payment.</p>
          <Link to={`/dashboard/orders/${ctx.order_id}`} className="btn-primary text-sm mt-6 inline-flex">View order <ArrowUpRight size={14} /></Link>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Link to="/dashboard/services" className="inline-flex items-center gap-1.5 text-sm text-inkSoft hover:text-ink mb-4"><ChevronLeft size={14} /> All services</Link>

      {/* Paid banner */}
      <div className="mb-5 rounded-2xl border border-emerald-200 bg-emerald-50/60 px-5 py-4 flex items-center gap-3" data-testid="payment-success-banner">
        <CheckCircle2 size={20} className="text-emerald-600" />
        <div className="flex-1">
          <div className="text-sm font-semibold text-emerald-900">Payment successful ✓</div>
          <div className="text-xs text-emerald-800/80">${((ctx?.amount || 0) / 100).toFixed(2)} charged. Fill your brief below and we&apos;ll get to work.</div>
        </div>
        {ctx?.receipt_url && (
          <a href={ctx.receipt_url} target="_blank" rel="noreferrer" className="text-xs text-emerald-900 underline">View receipt</a>
        )}
      </div>

      <form onSubmit={submitBrief} className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="brief-form">
        {/* Brief card */}
        <div className="lg:col-span-2 rounded-2xl border border-line bg-white overflow-hidden">
          <div className="bg-ink text-white px-6 py-5">
            <h1 className="font-display text-xl font-semibold tracking-tight" data-testid="brief-title">{ctx?.package_name || "Your order brief"}</h1>
            <p className="text-white/70 text-xs mt-1">Fill in the details so our team can start fulfillment.</p>
          </div>
          <div className="p-6">
            {!form ? (
              <p className="text-sm text-inkSoft">No brief required for this service. Click confirm to submit.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {form.fields.map((f) => (
                  <DynamicField key={f.key} field={f} value={requirements[f.key] ?? ""} onChange={(v) => setField(f.key, v)} />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <aside className="rounded-2xl border border-line bg-white overflow-hidden h-max lg:sticky lg:top-6">
          <div className="bg-ink text-white px-6 py-5">
            <h2 className="font-display text-lg font-semibold tracking-tight">Confirmation</h2>
          </div>
          <div className="p-6 divide-y divide-line">
            <Row k="Service" v={service?.title || ctx?.service_slug} />
            <Row k="Package" v={pkg?.name || "—"} />
            <Row k="Amount paid" v={<span className="font-mono">${((ctx?.amount || 0) / 100).toFixed(2)}</span>} highlight />
            <Row k="Estimated completion" v={eta} />
          </div>
          <div className="p-6 pt-2">
            <button
              type="submit"
              disabled={submitting || orderCreated}
              className="btn-primary w-full justify-center text-sm"
              data-testid="brief-submit"
            >
              {submitting ? "Confirming…" : (<>Confirm order <ArrowUpRight size={16} /></>)}
            </button>
            <p className="text-[11px] text-inkSoft mt-3 text-center">We&apos;ll email you a confirmation and receipt.</p>
          </div>
        </aside>
      </form>
    </DashboardLayout>
  );
}

function DynamicField({ field, value, onChange }) {
  const common = "mt-2 w-full px-4 rounded-xl border border-line bg-white text-sm outline-none focus:border-ink";
  const inputCls = `${common} h-11`;
  const required = field.required;
  const span = field.full ? "sm:col-span-2" : "";
  if (field.type === "textarea") {
    return (
      <label className={`block ${span}`}>
        <span className="text-xs uppercase tracking-widest text-inkSoft">{field.label}{required && " *"}</span>
        <textarea rows={4} required={required} value={value} onChange={(e) => onChange(e.target.value)} className={`${common} py-3`} placeholder={field.placeholder} maxLength={field.max} data-testid={`req-${field.key}`} />
      </label>
    );
  }
  if (field.type === "select") {
    return (
      <label className={`block ${span}`}>
        <span className="text-xs uppercase tracking-widest text-inkSoft">{field.label}{required && " *"}</span>
        <select required={required} value={value} onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid={`req-${field.key}`}>
          <option value="">Select…</option>
          {field.options.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
        </select>
      </label>
    );
  }
  return (
    <label className={`block ${span}`}>
      <span className="text-xs uppercase tracking-widest text-inkSoft">{field.label}{required && " *"}</span>
      <input type={field.type || "text"} required={required} value={value} onChange={(e) => onChange(e.target.value)} className={inputCls} placeholder={field.placeholder} data-testid={`req-${field.key}`} />
    </label>
  );
}

function Row({ k, v, highlight }) {
  return (
    <div className={`flex items-center justify-between py-3 ${highlight ? "bg-terra/5 px-3 -mx-3 rounded-lg" : ""}`}>
      <span className="text-sm text-ink/80">{k}</span>
      <span className={`text-sm ${highlight ? "font-semibold text-ink" : "font-medium text-ink"}`}>{v}</span>
    </div>
  );
}
